import { Link } from "@tanstack/react-router";
import { cn } from "@wags/ui/ui-utils";
import { usePostHog } from "posthog-js/react";
import { useRef } from "react";
import { useScrollFade } from "@/components/shared/useScrollFade";
import { DogPhoto } from "./dog-photo";
import {
    captureMoreDogsClicked,
    type MoreDogsSurface,
} from "./more-dogs-analytics";
import { otherDogsLabel } from "./other-dogs";
import type { ProviderOtherDogs } from "./use-provider-other-dogs";

const TILE = "w-22 shrink-0 snap-start";
const FOCUS =
    "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow";

export function FamilyRow({
    otherDogs,
    providerId,
    sourceListingId,
    surface,
    current,
}: {
    otherDogs: ProviderOtherDogs;
    providerId: string;
    sourceListingId: string;
    surface: MoreDogsSurface;
    current: {
        name: string;
        photoUrl: string | undefined;
        providerKind: "breeder" | "shelter";
    };
}) {
    const posthog = usePostHog();
    const railRef = useRef<HTMLUListElement>(null);
    const dogCount = otherDogs.status === "ready" ? otherDogs.dogs.length : 0;
    useScrollFade(railRef, "x", [dogCount]);
    if (otherDogs.status !== "ready") return null;

    const hidden = otherDogs.availableOthers - otherDogs.dogs.length;
    return (
        <div className="pt-1 pb-2">
            <p className="px-3 text-caption text-doberman-black/55">
                {otherDogsLabel({
                    dogName: current.name,
                    availableOthers: otherDogs.availableOthers,
                    littermates: otherDogs.littermates,
                    providerKind: current.providerKind,
                })}
            </p>
            <ul
                ref={railRef}
                data-scroll-restoration-id={`family-row-${sourceListingId}`}
                className="scroll-fade-x -mx-1 mt-1.5 flex snap-x scroll-px-4 gap-2 overflow-x-auto px-4 pt-1.5 pb-1 scrollbar-none"
            >
                <li className={TILE} aria-current="page">
                    <span className="flex flex-col gap-1.5">
                        <span className="relative block size-22 rounded-sm outline-2 outline-offset-2 outline-labrador-yellow">
                            <span className="img-inset block size-full overflow-hidden rounded-sm bg-havanese">
                                <DogPhoto
                                    url={current.photoUrl}
                                    size={176}
                                    listingMedia
                                />
                            </span>
                            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-sm bg-labrador-yellow px-1.5 py-0.5 text-caption leading-none font-semibold text-doberman-black">
                                Viewing
                            </span>
                        </span>
                        <span className="mt-1 truncate text-center text-caption font-medium text-doberman-black">
                            {current.name}
                        </span>
                    </span>
                </li>
                {otherDogs.dogs.map((dog, position) => (
                    <li key={dog.id} className={TILE}>
                        <Link
                            to="/dog/$dogId"
                            params={{ dogId: dog.id }}
                            onClick={() =>
                                captureMoreDogsClicked(posthog, {
                                    kind: "dog",
                                    surface,
                                    position,
                                    sourceListingId,
                                    targetListingId: dog.id,
                                })
                            }
                            className={cn("group flex flex-col gap-1.5", FOCUS)}
                        >
                            <span className="img-inset block size-22 overflow-hidden rounded-sm bg-havanese transition-transform duration-150 ease-out group-active:scale-96 motion-reduce:transition-none">
                                <DogPhoto
                                    url={dog.imageUrl}
                                    size={176}
                                    listingMedia
                                />
                            </span>
                            <span className="mt-1 truncate text-center text-caption text-doberman-black/75">
                                {dog.name}
                            </span>
                        </Link>
                    </li>
                ))}
                {hidden > 0 && (
                    <li className={TILE}>
                        <Link
                            to="/provider/$providerId"
                            params={{ providerId }}
                            aria-label={`See all ${otherDogs.availableOthers.toLocaleString()} of their dogs`}
                            onClick={() =>
                                captureMoreDogsClicked(posthog, {
                                    kind: "see_all",
                                    surface,
                                    sourceListingId,
                                })
                            }
                            className={cn("group flex flex-col gap-1.5", FOCUS)}
                        >
                            <span className="flex size-22 flex-col items-center justify-center rounded-sm bg-doberman-black/[0.05] transition-[background-color,transform] duration-150 ease-out group-active:scale-96 [@media(hover:hover)]:group-hover:bg-doberman-black/[0.08] motion-reduce:transition-none">
                                <span className="text-label text-doberman-black tabular-nums">
                                    +{hidden.toLocaleString()}
                                </span>
                                <span className="text-caption text-doberman-black/55">
                                    See all
                                </span>
                            </span>
                        </Link>
                    </li>
                )}
            </ul>
        </div>
    );
}
